/**
 * Product like handler (product likes toggle)
 */

import { notifier } from "../../../core/notifier/notifier";
import { toPositiveIntOrNull } from "../../../util/id-utils";
import { ProductLikeApiService } from "./product-like-api";
import { ProductLikeRender } from "./product-like-render";

type ProductLikeApiLike = {
    addLike(productId: number): Promise<Response>;
    removeLike(productId: number): Promise<Response>;
};

type ProductLikeRenderLike = {
    setButtonState(btn: HTMLElement, liked: boolean): void;
};

type BoolLikeValue = string | boolean | number | null | undefined;

class ProductLikeHandler {
    public apiService: ProductLikeApiLike;

    public uiManager: ProductLikeRenderLike;

    public constructor(apiService?: ProductLikeApiLike, uiManager?: ProductLikeRenderLike) {
        this.apiService = apiService || ProductLikeApiService;
        this.uiManager = uiManager || ProductLikeRender;
    }

    public parseBool(value: BoolLikeValue): boolean {
        return String(value || "").toLowerCase() === "true";
    }

    public getLikedClass(btn: HTMLElement): string {
        return (btn.dataset.likedClass || "").trim() || "products-liked";
    }

    public async handleLike(btn: HTMLElement): Promise<void> {
        const productId = toPositiveIntOrNull(btn.dataset.productId);
        if (productId === null) return;

        const likedClass = this.getLikedClass(btn);
        const isLiked = btn.dataset.isLiked === "true" || btn.classList.contains(likedClass);

        if (btn instanceof HTMLButtonElement && (btn.disabled || btn.classList.contains("loading"))) {
            return;
        }

        const originalIsLiked = isLiked;
        const optimisticIsLiked = !isLiked;

        this.uiManager.setButtonState(btn, optimisticIsLiked);

        if (btn instanceof HTMLButtonElement) {
            btn.disabled = true;
        }
        btn.classList.add("loading");

        try {
            if (!originalIsLiked) {
                const res = await this.apiService.addLike(productId);
                if (!res.ok) throw new Error("Failed to like product");
            } else {
                const res = await this.apiService.removeLike(productId);
                if (!res.ok) throw new Error("Failed to unlike product");
            }
        } catch (err) {
            this.uiManager.setButtonState(btn, originalIsLiked);
            notifier.error("like_update_failed");
            console.error(err);
        } finally {
            btn.classList.remove("loading");
            if (btn instanceof HTMLButtonElement) {
                btn.disabled = false;
            }
        }
    }
}

export { ProductLikeHandler };
